import { READINESS_EXIT_DURATION_MS } from 'modules/overlay-v2/domain/constants/readiness';
import { useEffect, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

/** Параметр ссылки оверлея и панели: `loaderPreview=1` или `loaderPreview=loop`. */
export const LOADER_PREVIEW_QUERY_PARAM = 'loaderPreview';

/** Сколько знак держится на экране в режиме `loop`, прежде чем уйти. */
export const LOADER_PREVIEW_HOLD_MS = 3_000;
/** Пауза без знака между кругами `loop` — чтобы появление было видно заново. */
export const LOADER_PREVIEW_GAP_MS = 500;

export interface MatchSwitchLoaderPreview {
  isVisible: boolean;
  isClosing: boolean;
  /** Номер круга: ключ компонента, чтобы анимация появления проигрывалась заново. */
  cycle: number;
}

/**
 * Показ знака переключения матча без переключения матча. В жизни он виден секунду-две,
 * а проверить нужно блик, уход, прозрачный фон в OBS и «уменьшить движение».
 *
 * - `?loaderPreview=1` — знак висит постоянно;
 * - `?loaderPreview=loop` — появляется, держится `LOADER_PREVIEW_HOLD_MS`, уходит и повторяет.
 *
 * Возвращает `undefined`, если параметра нет: тогда экран подготовки работает как обычно.
 */
function useMatchSwitchLoaderPreview(): MatchSwitchLoaderPreview | undefined {
  const [mode] = useQueryParam(LOADER_PREVIEW_QUERY_PARAM, StringParam);
  const isLoop = mode === 'loop';
  const isEnabled = mode === '1' || isLoop;

  const [phase, setPhase] = useState<'shown' | 'closing' | 'hidden'>('shown');
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (!isLoop) return;

    const delay = { shown: LOADER_PREVIEW_HOLD_MS, closing: READINESS_EXIT_DURATION_MS, hidden: LOADER_PREVIEW_GAP_MS }[phase];
    const timer = setTimeout(() => {
      if (phase === 'shown') setPhase('closing');
      if (phase === 'closing') setPhase('hidden');
      if (phase === 'hidden') {
        setCycle((value) => value + 1);
        setPhase('shown');
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [isLoop, phase]);

  if (!isEnabled) return undefined;
  if (!isLoop) return { isVisible: true, isClosing: false, cycle: 0 };
  return { isVisible: phase !== 'hidden', isClosing: phase === 'closing', cycle };
}

export default useMatchSwitchLoaderPreview;
